import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import * as S from "./HomePage.style";
import {
  GET_NOTICE_BOARDS,
  GET_USER,
  GET_CALENDAR,
  GET_SUB_SCHEDULE,
} from "src/common/querykeys";
import { request } from "src/common/api";
import { BaseResponse } from "src/common/types/baseRespones";
import { noticesBoard } from "src/common/types/noticesBoard";
import { calendarEvent } from "src/common/types/calendarEvent";
import { stmUser } from "src/common/types/stmUser";
import { SubSchedule } from "src/common/types/subSchedules";
import { NoticeUpdateModal } from "src/components/containers/NoticesBoard/NoticeUpdateModal";
import { MemberModal } from "./Modal/MemberModal";

const HomePage = () => {
  const { data: noticeBoardList } = useQuery({
    queryKey: [GET_NOTICE_BOARDS],
    queryFn: async () => {
      const result = await request<BaseResponse>({
        method: "GET",
        url: "/notice-boards",
      });

      return result.data;
    },
    initialData: [] as noticesBoard[],
  });

  const { data: calendarEventList } = useQuery({
    queryKey: [GET_CALENDAR],
    queryFn: async () => {
      const result = await request<BaseResponse>({
        method: "GET",
        url: "/calendar",
        params: { currentTime: currentTime.toISOString().slice(0, 10) },
      });

      return result.data;
    },
    initialData: [] as calendarEvent[],
  });

  const { data: stmUserList } = useQuery({
    queryKey: [GET_USER],
    queryFn: async () => {
      const result = await request<BaseResponse>({
        method: "GET",
        url: "/stm-user",
      });

      return result.data;
    },
    initialData: [] as stmUser[],
  });

  const { data: subScheduleList } = useQuery({
    queryKey: [GET_SUB_SCHEDULE],
    queryFn: async () => {
      const result = await request<BaseResponse>({
        method: "GET",
        url: "/sub-schedules",
        params: {
          searchInputs: {
            searchDateStart: currentTime.toISOString().slice(0, 10),
            searchDateEnd: currentTime.toISOString().slice(0, 10),
          },
        },
      });
      return result.data;
    },
    initialData: [] as SubSchedule[],
  });

  const [currentTime, setCurrentTime] = useState(new Date());
  const [noticeModalShow, setNoticeModalShow] = useState<boolean>(false);
  const [selectedNoticeBoards, setSelectedNoticeBoards] =
    useState<noticesBoard>();

  const [memberModalShow, setMemberModalShow] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<stmUser | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "long",
    });
  };

  const handleNoticeClick = (notice: noticesBoard) => {
    setSelectedNoticeBoards(notice);
    setNoticeModalShow(true);
  };

  const handleMemberClick = (member: stmUser) => {
    setSelectedMember(member);
    setMemberModalShow(true);
  };

  return (
    <>
      <S.HomeContainer>
        <S.WelcomeSection>
          <S.MainTitle>프로젝트 관리 시스템</S.MainTitle>
          <S.SubTitle>
            효율적인 협업과 성공적인 프로젝트 완성을 위한 통합 플랫폼
          </S.SubTitle>
          <S.CurrentTime>
            📅 {formatDate(currentTime)} | ⏰ {formatTime(currentTime)}
          </S.CurrentTime>
        </S.WelcomeSection>

        <S.CardGrid>
          {/* 공지사항 카드 */}
          <S.Card>
            <S.CardHeader>
              <S.CardIcon>📢</S.CardIcon>
              <S.CardTitle>공지사항</S.CardTitle>
            </S.CardHeader>
            {noticeBoardList && noticeBoardList.length > 0 ? (
              <S.NoticeList>
                {noticeBoardList.slice(0, 5).map((notice, index) => (
                  <S.NoticeItem
                    key={notice.oid || index}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    onClick={() => handleNoticeClick(notice)} // 👈 클릭 이벤트
                  >
                    <S.NoticeTitle>{notice.noticeTitle}</S.NoticeTitle>
                    <S.NoticeTitle>{notice.noticeDescription}</S.NoticeTitle>
                    <S.NoticeDate>
                      📅{" "}
                      {notice.createdAt
                        ? new Date(notice.createdAt).toLocaleDateString("ko-KR")
                        : ""}
                    </S.NoticeDate>
                  </S.NoticeItem>
                ))}
                {noticeBoardList.length > 5 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "12px",
                      color: "#64748b",
                      fontSize: "0.9rem",
                    }}
                  >
                    + {noticeBoardList.length - 5}개의 공지사항 더 보기
                  </div>
                )}
              </S.NoticeList>
            ) : (
              <S.LoadingContainer>📱 공지사항이 없습니다.</S.LoadingContainer>
            )}
          </S.Card>

          {/* 팀멤버 현황 카드 */}
          <S.Card>
            <S.CardHeader>
              <S.CardIcon>👥</S.CardIcon>
              <S.CardTitle>팀멤버 현황</S.CardTitle>
            </S.CardHeader>
            <S.MemberGrid>
              {Array.isArray(stmUserList) && stmUserList.length > 0 ? (
                stmUserList
                  .filter((member) => member.useYn === true)
                  .filter((member) => member.userId !== "dev")
                  .map((member, index) => (
                    <S.MemberCard
                      key={member.oid}
                      style={{ animationDelay: `${index * 0.1}s` }}
                      onClick={() => handleMemberClick(member)}
                    >
                      <S.MemberName>
                        {member.userName}
                        <S.OnlineStatus />
                      </S.MemberName>
                      {/* <S.MemberTel>{member.hp}</S.MemberTel> */}
                      <S.MemberTel>
                        {subScheduleList
                          ?.filter((sub) => sub.assignedTo === member.oid)
                          .map((sub) => sub.subScheduleName)
                          .join(", ")}
                      </S.MemberTel>
                    </S.MemberCard>
                  ))
              ) : (
                <S.LoadingContainer>팀멤버 정보가 없습니다.</S.LoadingContainer>
              )}
            </S.MemberGrid>
          </S.Card>

          {/* 캘린더 일정 카드 */}
          <S.Card>
            <S.CardHeader>
              <S.CardIcon>📅</S.CardIcon>
              <S.CardTitle>오늘의 일정</S.CardTitle>
            </S.CardHeader>
            <S.CalendarContainer>
              {Array.isArray(calendarEventList) &&
              calendarEventList.length > 0 ? (
                calendarEventList.map((event, index) => (
                  <S.EventItem
                    key={event.oid || index}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <S.EventTime>
                      🕐 {event.employeeWorkStart} ~ {event.employeeWorkEnd}
                    </S.EventTime>
                    <S.EventTitle>
                      [{event.userName.toString()}] {event.workTitle}
                    </S.EventTitle>
                    <S.EventDescription>
                      {event.employeeDescription}
                    </S.EventDescription>
                  </S.EventItem>
                ))
              ) : (
                <S.LoadingContainer>
                  📅 오늘 일정이 없습니다.
                </S.LoadingContainer>
              )}
            </S.CalendarContainer>
          </S.Card>
        </S.CardGrid>
      </S.HomeContainer>
      {noticeModalShow && selectedNoticeBoards && (
        <NoticeUpdateModal
          selectedNoticeBoards={selectedNoticeBoards}
          setModalShow={setNoticeModalShow}
          updateModalShow={noticeModalShow}
          formId="update_notice_boards"
        />
      )}

      {memberModalShow && selectedMember && (
        <MemberModal
          member={selectedMember}
          scheduleList={subScheduleList}
          show={memberModalShow}
          onClose={() => {
            setMemberModalShow(false);
            setSelectedMember(null);
          }}
        />
      )}
    </>
  );
};

export default HomePage;
